// A PO's emails back and forth (Mitch, 2026-11-04: "There needs to be a communication tab which shows the email history
// back and forth"): every email HHL Ops sent for it, with what it said, and every reply found on its Gmail thread (who,
// when and Gmail's preview of it: HHL Ops reads replies' headers and snippet only, Open in Gmail has the rest), newest
// first, with what the vendor told us about the order (confirmed, a new ship date, units built, shipped, a note), one line
// each, from the vendor portal or entered for them. Opening it marks their updates seen. The Messages tab shows it under
// the last send's strip.
import { ArrowDownLeft, ArrowUpRight, ExternalLink, MessageSquare } from "lucide-react";
import { useEffect, useState } from "react";
import type { Po } from "../../../../shared/db.gen.ts";
import { gmailThreadUrl } from "../../../../shared/po-email.ts";
import { ago, personName } from "../../lib/format.ts";
import { can, mutate } from "../../lib/mutate.ts";
import { useRows } from "../../lib/sync.ts";
import { cx } from "../../ui/controls.tsx";
import { describe, ORDER_TEXT_STAFF } from "../../ui/OrderUpdates.tsx";
import { usePoEmails } from "./PoEmail.tsx";

type Message =
  | {
      kind: "sent";
      id: string;
      at: string;
      who: string;
      from: string;
      to: string;
      subject: string;
      body: string;
      status: string;
      error: string;
      url: string | null;
    }
  | { kind: "reply"; id: string; at: string; who: string; from: string; snippet: string; url: string }
  | { kind: "update"; id: string; at: string; who: string; theirs: boolean; text: string };

/** The PO's emails and their replies, newest first. */
export function PoMessages({ po }: { po: Po }) {
  const { emails, replies } = usePoEmails(po.id);
  const users = useRows("app_user");
  const contacts = useRows("vendor_contact");
  const updates = useRows("po_update").filter((u) => u.po_id === po.id);
  const unseen = updates.some((u) => u.contact_id && !u.seen_at);
  useEffect(() => {
    if (unseen && can("po_update.seen")) void mutate("po_update.seen", { po_id: po.id });
  }, [unseen, po.id]);
  const mailboxOf = (emailId: string) => emails.find((e) => e.id === emailId)?.from_email ?? emails.find((e) => e.status === "sent")?.from_email ?? "";
  const list: Message[] = [
    ...emails.map(
      (e): Message => ({
        kind: "sent",
        id: e.id,
        at: e.sent_at ?? e.started_at,
        who: personName(users, e.sent_by),
        from: e.from_email,
        to: [...e.to_emails, ...e.cc_emails].join(", "),
        subject: e.subject,
        body: e.body,
        status: e.status,
        error: e.error,
        url: e.gmail_thread_id ? gmailThreadUrl(e.from_email, e.gmail_thread_id) : null,
      }),
    ),
    ...updates.map((u): Message => {
      const c = contacts.find((x) => x.id === u.contact_id);
      const view = { ...u, from_them: !!u.contact_id, by_name: c ? c.name || c.email : personName(users, u.created_by) } as Parameters<typeof describe>[0];
      const what = describe(view, ORDER_TEXT_STAFF);
      return {
        kind: "update",
        id: u.id,
        at: u.created_at,
        who: view.by_name,
        theirs: !!u.contact_id,
        text: [ORDER_TEXT_STAFF.kinds[u.kind], what, u.note ? `“${u.note}”` : ""].filter(Boolean).join(" · "),
      };
    }),
    ...replies.map(
      (r): Message => ({
        kind: "reply",
        id: r.id,
        at: r.received_at,
        who: r.from_name || r.from_email,
        from: r.from_email,
        snippet: r.snippet,
        url: gmailThreadUrl(mailboxOf(r.po_email_id), r.gmail_thread_id),
      }),
    ),
  ].sort((a, b) => b.at.localeCompare(a.at));
  if (!list.length)
    return (
      <p className="px-4 py-3 text-[12px] text-faint">
        {po.sent_revision == null
          ? "Nothing sent yet: Send to vendor emails the PO from here."
          : "No emails from HHL Ops for this PO (it was sent another way)."}
      </p>
    );
  return (
    <ol className="flex flex-col gap-2 px-4 py-2">
      {list.map((m) => (
        <MessageRow key={m.id} m={m} />
      ))}
    </ol>
  );
}

function MessageRow({ m }: { m: Message }) {
  const [open, setOpen] = useState(false);
  if (m.kind === "update")
    return (
      <li className="flex items-center gap-2 px-3 py-1 text-[12px]">
        <MessageSquare className="size-3.5 shrink-0 text-faint" />
        <span className="min-w-0 flex-1 truncate">
          <span className="font-medium">{m.who}</span>
          {m.theirs ? " (vendor portal)" : " (for them)"}: {m.text}
        </span>
        <span className="shrink-0 text-faint" title={new Date(m.at).toLocaleString()}>
          {ago(m.at)}
        </span>
      </li>
    );
  const theirs = m.kind === "reply";
  const Icon = theirs ? ArrowDownLeft : ArrowUpRight;
  return (
    <li className={cx("rounded-md border px-3 py-2 text-[12px]", theirs ? "border-accent/30 bg-accent-soft/40" : "border-line bg-surface")}>
      <div className="flex min-w-0 items-center gap-2">
        <Icon className={cx("size-3.5 shrink-0", theirs ? "text-accent-text" : "text-muted")} />
        <span className="min-w-0 truncate font-medium">{theirs ? m.who : `${m.who} → ${m.to}`}</span>
        {m.kind === "sent" && m.status !== "sent" && (
          <span className={cx("shrink-0", m.status === "failed" ? "text-danger" : "text-warn")}>{m.status === "failed" ? "didn't go" : "sending"}</span>
        )}
        <span className="ml-auto shrink-0 text-faint" title={new Date(m.at).toLocaleString()}>
          {ago(m.at)}
        </span>
        {m.url && (
          <a href={m.url} target="_blank" rel="noreferrer" className="inline-flex shrink-0 items-center gap-0.5 text-accent-text hover:underline">
            Gmail <ExternalLink className="size-3" />
          </a>
        )}
      </div>
      {m.kind === "sent" ? (
        <div className="mt-1 pl-5">
          <div className="text-muted">
            {m.subject} <span className="text-faint">· from {m.from}</span>
          </div>
          {m.error && m.status !== "sent" && <div className="text-danger">{m.error}</div>}
          <button type="button" onClick={() => setOpen(!open)} className="mt-0.5 text-left text-accent-text hover:underline">
            {open ? "Hide what it said" : "Show what it said"}
          </button>
          {open && <pre className="mt-1 font-sans text-[12px] whitespace-pre-wrap text-text">{m.body}</pre>}
        </div>
      ) : (
        <div className="mt-1 pl-5 text-text">{m.snippet ? `“${m.snippet}”` : <span className="text-faint">(no preview)</span>}</div>
      )}
    </li>
  );
}
